import { muiTheme } from "@/styles/theme";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  CircularProgress,
  IconButton,
  Popper,
  SxProps,
  Tooltip,
  Typography,
  useTheme,
  Paper,
  Stack,
  ClickAwayListener,
  Checkbox,
  FormControlLabel,
  TextField,
  Skeleton,
  InputAdornment,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import debounce from "lodash/debounce";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { NwButton } from "@nawy/ui";
import CustomLabel from "@/modules/common/components/input/custom-label";
import { SearchOutlined } from "@mui/icons-material";
import Color from "color";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";

// ==================== TYPES ====================
export type Option = {
  label: string;
  value: string;
  color?: string;
  disabled?: boolean;
};

type AutoCompleteProps = {
  options: Option[];
  multiple?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  handleSearch?: (value: string) => void;
  placeholder: string;
  onChange?: (selected: Option[] | Option | null) => void;
  label?: string;
  value?: Option[] | Option | null;
  defaultValue?: Option[] | Option | null;
  error?: boolean;
  helperText?: string;
  valueIds?: string[] | string | null;
  required?: boolean;
  disableClearable?: boolean;
  disabled?: boolean;
  customPlaceholderSx?: SxProps;
  variant?: "default" | "actionableSummary";
  renderOption?: (option: Option) => React.ReactNode;
  onToggleOption?: (newSelection: Option[]) => Option[];
  row?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onClear?: () => void;
  showTooltip?: boolean;
  uniqueOptionsOnly?: boolean;
  disableClientSideFiltering?: boolean;
  popperMinWidth?: string;
  labelSx?: SxProps;
  closeOnSelect?: boolean;
  chipSx?: SxProps;
};

// ==================== CONSTANTS ====================
const POPPER_OFFSET = [0, 6];
const DEBOUNCE_DELAY = 300;
const MAX_DISPLAYED_ITEMS = 2;
const OPTION_HEIGHT = "36px";
const DROPDOWN_WIDTH = "320px";
const MAX_DROPDOWN_HEIGHT = 250;

// ==================== UTILITY FUNCTIONS ====================
const getSummarizedText = (selectedOptions: Option[]): string => {
  if (!selectedOptions?.length) return "";

  if (selectedOptions.length <= MAX_DISPLAYED_ITEMS) {
    return selectedOptions.map((option) => option.label).join(", ");
  }

  const displayedItems = selectedOptions.slice(0, MAX_DISPLAYED_ITEMS);
  const remainingCount = selectedOptions.length - MAX_DISPLAYED_ITEMS;
  const displayedText = displayedItems.map((option) => option.label).join(", ");

  return `${displayedText}, +${remainingCount}`;
};

const getInitialValue = (
  multiple: boolean,
  defaultValue: Option[] | Option | null | undefined,
  isControlled: boolean,
): Option[] | Option | null => {
  if (!isControlled && defaultValue !== undefined) {
    return defaultValue;
  }
  return multiple ? [] : null;
};

// ==================== CUSTOM POPPERS ====================
const createCustomPopper = (minWidth?: string) => {
  const CustomPopper = (props: any) => (
    <Popper
      {...props}
      placement="bottom-start"
      modifiers={[
        {
          name: "offset",
          options: { offset: POPPER_OFFSET },
        },
      ]}
      style={{
        ...props.style,
        ...(minWidth ? { minWidth } : {}),
        border: "1px solid #E0E0E0",
        borderRadius: muiTheme.spacing(5),
      }}
    />
  );
  CustomPopper.displayName = "CustomPopper";
  return CustomPopper;
};

const CustomPopper = createCustomPopper();

const CustomActionableSummaryPopper = (props: any) => (
  <Popper
    {...props}
    placement="bottom-start"
    modifiers={[
      {
        name: "offset",
        options: { offset: POPPER_OFFSET },
      },
    ]}
    sx={{ zIndex: 1300 }}
  >
    {props.children}
  </Popper>
);

// ==================== SUB-COMPONENTS ====================
const LabelWithRequired = ({
  label,
  required,
  sx,
}: {
  label?: string;
  required?: boolean;
  sx?: SxProps;
}) => {
  if (!label) return null;

  return (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      <CustomLabel sx={sx}>{label}</CustomLabel>
      {required && <CustomLabel sx={{ color: "red" }}>*</CustomLabel>}
    </Box>
  );
};

const ActionableSummaryDropdown = ({
  options,
  tempValue,
  onToggle,
  onReset,
  onCancel,
  onApply,
  handleKeyDown,
  highlightedIndex,
  setHighlightedIndex,
  dropdownRef,
  inputValue,
  setInputValue,
  debouncedSearch,
  loading,
  renderOption,
}: {
  options: Option[];
  tempValue: Option[];
  onToggle: (option: Option) => void;
  onReset: () => void;
  onCancel: () => void;
  onApply: () => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  highlightedIndex: number;
  setHighlightedIndex: (i: number) => void;
  dropdownRef: React.RefObject<HTMLElement | null>;
  inputValue: string;
  setInputValue: (val: string) => void;
  debouncedSearch: (val: string) => void;
  loading: boolean;
  renderOption?: (option: Option) => React.ReactNode;
}) => {
  const theme = useTheme();
  const sortedOptions = useMemo(() => {
    const selectedSet = new Set(tempValue.map((o) => o.value));

    // Filter options by input, but always keep selected items
    const filteredByInput = inputValue
      ? options.filter(
          (o) =>
            selectedSet.has(o.value) ||
            o.label.toLowerCase().includes(inputValue.toLowerCase()),
        )
      : options;

    return [
      ...filteredByInput.filter((o) => selectedSet.has(o.value)),
      ...filteredByInput.filter((o) => !selectedSet.has(o.value)),
    ];
  }, [options, tempValue, inputValue]);

  const optionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    if (highlightedIndex >= 0 && highlightedIndex < sortedOptions.length) {
      const opt = sortedOptions[highlightedIndex];
      const el = optionRefs.current[opt.value];
      if (el) {
        el.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, sortedOptions]);

  return (
    <Paper
      elevation={3}
      ref={dropdownRef as any}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      sx={{
        mt: 1,
        width: DROPDOWN_WIDTH,
        borderRadius: theme.nawy.radius.lg,
        backgroundColor: theme.nawy.colors.semantic.layer.primary,
        overflow: "hidden",
        outline: "none",
      }}
    >
      {/* Search input */}
      <Box sx={{ p: theme.nawy.spacing.sm }}>
        <TextField
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              fontSize: "14px",
              "& fieldset": { borderColor: theme.palette.grey[300] },
              "&:hover fieldset": { borderColor: theme.palette.grey[400] },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
            "& .MuiInputBase-input": { paddingLeft: "12px" },
            "& .MuiInputAdornment-root": { marginLeft: 0 },
          }}
          fullWidth
          size="small"
          autoFocus
          variant="outlined"
          placeholder="Search"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            debouncedSearch(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Options list */}
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          px: theme.nawy.spacing.xs,
          maxHeight: MAX_DROPDOWN_HEIGHT,
          overflowY: "auto",
          scrollbarColor: `${theme.nawy.colors.semantic.overlay.primary60} transparent`,
        }}
      >
        {loading ? (
          <Box sx={{ p: 2 }}>
            <Stack spacing={1}>
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={30} />
              ))}
            </Stack>
          </Box>
        ) : (
          sortedOptions.map((option, idx) => {
            const isHighlighted = highlightedIndex === idx;
            return (
              <Box
                key={option.value}
                ref={(el: HTMLElement | null) =>
                  (optionRefs.current[option.value] = el)
                }
                onMouseEnter={() => setHighlightedIndex(idx)}
                sx={{
                  borderRadius: theme.nawy.radius.md,
                  backgroundColor: isHighlighted
                    ? theme.nawy.colors.semantic.layer.secondary
                    : "transparent",
                  px: 0.5,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempValue.some((v) => v.value === option.value)}
                      onChange={() => onToggle(option)}
                      size="medium"
                      sx={{
                        color: theme.nawy.colors.semantic.border.primaryDark,
                        "&.Mui-checked": {
                          color: theme.nawy.colors.semantic.layer.accent,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        textOverflow: "ellipsis",
                        ...theme.nawy.typography.english.variants.body.md
                          .regular,
                        color: theme.nawy.colors.semantic.text.primary,
                      }}
                    >
                      {renderOption ? renderOption(option) : option.label}
                    </Typography>
                  }
                />
              </Box>
            );
          })
        )}
      </Box>

      {/* Footer buttons */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        py={theme.nawy.spacing.xs}
        pr={theme.nawy.spacing.xs}
        gap={theme.nawy.spacing.xs}
        borderTop={`1px solid ${theme.nawy.colors.semantic.border.secondary}`}
        bgcolor={theme.nawy.colors.semantic.layer.primary}
      >
        <Typography
          variant="body2"
          sx={{
            ...theme.nawy.typography.english.variants.body.md.semibold,
            color: theme.nawy.colors.semantic.text.accent,
            cursor: "pointer",
            "&:hover": {
              color: theme.nawy.colors.semantic.text.primary,
            },
            margin: 2,
          }}
          onClick={onReset}
        >
          Clear
        </Typography>

        <Stack direction="row" gap={theme.nawy.spacing.xs}>
          <NwButton variant="outlined" onClick={onCancel}>
            Cancel
          </NwButton>
          <NwButton onClick={onApply}>
            Save
          </NwButton>
        </Stack>
      </Stack>
    </Paper>
  );
};

// ==================== CUSTOM HOOKS ====================
const useAutoCompleteState = (
  value: Option[] | Option | null | undefined,
  defaultValue: Option[] | Option | null | undefined,
  multiple: boolean,
) => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<Option[] | Option | null>(
    () => getInitialValue(multiple, defaultValue, isControlled),
  );

  useEffect(() => {
    if (isControlled) {
      setInternalValue(value);
    }
  }, [value, isControlled]);

  return {
    isControlled,
    internalValue,
    setInternalValue,
    autocompleteValue: isControlled ? value : internalValue,
  };
};

const useKeyboardNavigation = (
  open: boolean,
  filteredOptions: Option[],
  autocompleteValue: Option[] | Option | null,
  tempValue: Option[],
  isActionableSummary: boolean,
  isEditing: boolean,
  multiple: boolean,
  handleChange: (event: any, newValue: Option[] | Option | null) => void,
  setOpen: (open: boolean) => void,
  handleCancel: () => void,
) => {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useEffect(() => {
    if (open && filteredOptions.length > 0) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [open, filteredOptions]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!open) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev,
          );
          break;
        case "ArrowUp":
          event.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case "Enter":
          event.preventDefault();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < filteredOptions.length
          ) {
            const selectedOption = filteredOptions[highlightedIndex];
            if (isActionableSummary && multiple) {
              const currentValues =
                (isActionableSummary && isEditing
                  ? tempValue
                  : (autocompleteValue as Option[])) || [];
              const isAlreadySelected = currentValues.some(
                (v) => v.value === selectedOption.value,
              );
              const newValue = isAlreadySelected
                ? currentValues.filter((v) => v.value !== selectedOption.value)
                : [...currentValues, selectedOption];

              handleChange(event, newValue);
            } else if (multiple) {
              const currentValues = (autocompleteValue as Option[]) || [];
              const isAlreadySelected = currentValues.some(
                (v) => v.value === selectedOption.value,
              );
              const newValue = isAlreadySelected
                ? currentValues.filter((v) => v.value !== selectedOption.value)
                : [...currentValues, selectedOption];
              handleChange(event, newValue);
            } else {
              handleChange(event, selectedOption);
              setOpen(false);
            }
          }
          break;
        case "Escape":
          event.preventDefault();
          handleCancel();
          break;
      }
    },
    [
      open,
      filteredOptions,
      highlightedIndex,
      multiple,
      isActionableSummary,
      isEditing,
      tempValue,
      autocompleteValue,
      handleChange,
      setOpen,
      handleCancel,
    ],
  );

  return {
    highlightedIndex,
    setHighlightedIndex,
    handleKeyDown,
  };
};

// ==================== MAIN COMPONENT ====================
const CustomAutoComplete = ({
  options,
  multiple = false,
  fullWidth = true,
  loading,
  handleSearch,
  placeholder,
  onChange,
  label,
  value,
  defaultValue,
  error = false,
  valueIds,
  helperText = "",
  required = false,
  disableClearable = false,
  disabled = false,
  customPlaceholderSx,
  variant = "default",
  renderOption,
  onToggleOption,
  row = false,
  onOpen,
  onClose,
  onClear,
  showTooltip = true,
  uniqueOptionsOnly = false,
  disableClientSideFiltering = false,
  popperMinWidth,
  labelSx,
  closeOnSelect = false,
  chipSx,
}: AutoCompleteProps) => {
  const theme = useTheme();
  const isActionableSummary = variant === "actionableSummary" && multiple;

  const autocompleteRef = useRef<any>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLElement | null>(null);

  const { isControlled, setInternalValue, autocompleteValue } =
    useAutoCompleteState(value, defaultValue, multiple);

  const uniqueAutocompleteValue = useMemo(() => {
    if (!uniqueOptionsOnly) return autocompleteValue;
    if (!autocompleteValue) return autocompleteValue;
    if (Array.isArray(autocompleteValue)) {
      const seen = new Set();
      return autocompleteValue.filter((v) => {
        const key = v.label;
        const duplicate = seen.has(key);
        seen.add(key);
        return !duplicate;
      });
    }
    return autocompleteValue;
  }, [autocompleteValue, uniqueOptionsOnly]);

  const [tempValue, setTempValue] = useState<Option[]>([]);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(() => {
    const initial = isControlled ? value : defaultValue;
    if (!initial || Array.isArray(initial)) return "";
    return initial.label ?? "";
  });

  useEffect(() => {
    if (open || multiple) return;
    const selected = uniqueOptionsOnly ? uniqueAutocompleteValue : autocompleteValue;
    if (Array.isArray(selected)) return;
    setInputValue(selected?.label ?? "");
  }, [open, multiple, autocompleteValue, uniqueAutocompleteValue, uniqueOptionsOnly]);

  useEffect(() => {
    if (isActionableSummary && open) {
      setTempValue(
        ((uniqueOptionsOnly
          ? uniqueAutocompleteValue
          : autocompleteValue) as Option[]) || [],
      );
    }
  }, [
    open,
    autocompleteValue,
    uniqueAutocompleteValue,
    isActionableSummary,
    uniqueOptionsOnly,
  ]);

  const uniqueOptions = useMemo(() => {
    if (!uniqueOptionsOnly) return options;
    const seen = new Set();
    return options.filter((option) => {
      const key = option.label;
      const duplicate = seen.has(key);
      seen.add(key);
      return !duplicate;
    });
  }, [options, uniqueOptionsOnly]);

  const filteredOptionsDefault = useMemo(() => {
    const currentValue = uniqueOptionsOnly
      ? uniqueAutocompleteValue
      : autocompleteValue;
    const opts = uniqueOptionsOnly ? uniqueOptions : options;

    if (!multiple) return opts;

    return opts.filter((option) => {
      return !(currentValue as Option[] | null)?.some((selected) =>
        uniqueOptionsOnly
          ? selected.label === option.label
          : selected.value === option.value,
      );
    });
  }, [
    options,
    uniqueOptions,
    autocompleteValue,
    uniqueAutocompleteValue,
    multiple,
    uniqueOptionsOnly,
  ]);

  const dropdownOptions = useMemo(() => {
    let baseOptions = uniqueOptionsOnly ? uniqueOptions : options;

    if (inputValue.trim() && !handleSearch) {
      baseOptions = baseOptions.filter((o) =>
        o.label.toLowerCase().includes(inputValue.toLowerCase()),
      );
    }

    if (isActionableSummary) {
      const currentValue =
        (isEditing
          ? tempValue
          : ((uniqueOptionsOnly
              ? uniqueAutocompleteValue
              : autocompleteValue) as Option[])) || [];
      const selectedSet = new Set(currentValue.map((v) => v.value));
      const selectedOptions = baseOptions.filter((o) =>
        selectedSet.has(o.value),
      );
      const unselectedOptions = baseOptions.filter(
        (o) => !selectedSet.has(o.value),
      );
      return [...selectedOptions, ...unselectedOptions];
    }

    return baseOptions;
  }, [
    options,
    uniqueOptions,
    autocompleteValue,
    uniqueAutocompleteValue,
    tempValue,
    isActionableSummary,
    isEditing,
    inputValue,
    uniqueOptionsOnly,
    handleSearch,
  ]);

  const navOptions = isActionableSummary
    ? dropdownOptions
    : filteredOptionsDefault;

  const { highlightedIndex, setHighlightedIndex, handleKeyDown } =
    useKeyboardNavigation(
      open,
      navOptions,
      uniqueOptionsOnly ? uniqueAutocompleteValue : autocompleteValue,
      tempValue,
      isActionableSummary,
      isEditing,
      multiple,
      (_, newValue) => {
        if (isActionableSummary && isEditing) {
          setTempValue(newValue as Option[]);
        } else {
          if (!isControlled) setInternalValue(newValue);
          onChange?.(newValue);
        }
      },
      setOpen,
      () => {
        setTempValue(
          ((uniqueOptionsOnly
            ? uniqueAutocompleteValue
            : autocompleteValue) as Option[]) || [],
        );
        setOpen(false);
        setIsEditing(false);
      },
    );

  useEffect(() => {
    const targetValue = uniqueOptionsOnly
      ? uniqueAutocompleteValue
      : autocompleteValue;

    if (!multiple && !valueIds) {
      if (!isControlled && !targetValue) {
        setInternalValue(null);
      }
      return;
    }

    if (!Array.isArray(valueIds)) return;

    if (valueIds.length === 0) {
      if (!isControlled) setInternalValue([]);
      return;
    }

    const newValue = options.filter((option) =>
      valueIds.includes(option.value),
    );
    if (!isControlled) setInternalValue(newValue);
  }, [
    valueIds,
    multiple,
    options,
    isControlled,
    autocompleteValue,
    uniqueAutocompleteValue,
    uniqueOptionsOnly,
    setInternalValue,
  ]);

  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        handleSearch?.(searchValue);
      }, DEBOUNCE_DELAY),
    [handleSearch],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleChange = (_: any, newValue: Option[] | Option | null) => {
    if (isActionableSummary && isEditing) {
      setTempValue(newValue as Option[]);
    } else {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    }
  };

  const handleOpenDropdown = useCallback(
    (event?: React.MouseEvent) => {
      if (event) event.preventDefault();

      setIsEditing(true);
      setOpen(true);
      onOpen?.();

      if (isActionableSummary) {
        setTimeout(() => {
          dropdownRef.current?.focus?.();
        }, 0);
      } else {
        setTimeout(() => {
          if (autocompleteRef.current) {
            const input = autocompleteRef.current.querySelector("input");
            if (input) {
              input.focus();
              input.value = "";
            }
          }
        }, 0);
      }
    },
    [isActionableSummary, onOpen],
  );

  const handleClose = useCallback(() => {
    onClose?.();
    setOpen(false);
    setIsEditing(false);
  }, [onClose]);

  const handleApply = () => {
    if (!isControlled) {
      setInternalValue(tempValue);
    }
    onChange?.(tempValue);
    setOpen(false);
    setIsEditing(false);
    setInputValue("");
  };

  const handleCancel = () => {
    setTempValue(
      ((uniqueOptionsOnly
        ? uniqueAutocompleteValue
        : autocompleteValue) as Option[]) || [],
    );
    setInputValue("");
    handleClose();
  };

  const handleReset = () => {
    setTempValue([]);
    if (!isControlled) setInternalValue([]);
    onChange?.([]);
    onClear?.();
    setOpen(false);
    setIsEditing(false);
    setInputValue("");
  };

  const handleOptionToggle = (option: Option) => {
    const currentValues = tempValue || [];
    const isAlreadySelected = currentValues.some(
      (v) => v.value === option.value,
    );

    let newValue: Option[];

    if (isAlreadySelected) {
      newValue = currentValues.filter((v) => v.value !== option.value);
    } else {
      const newSelection = [...currentValues, option];
      if (onToggleOption) {
        newValue = onToggleOption(newSelection);
      } else {
        newValue = newSelection;
      }
    }
    setTempValue(newValue);
  };

  if (isActionableSummary) {
    const selectedOptions =
      ((uniqueOptionsOnly
        ? uniqueAutocompleteValue
        : autocompleteValue) as Option[]) || [];
    const hasSelection = selectedOptions.length > 0;

    const renderActionableSummaryField = () => (
      <Box
        ref={anchorRef}
        onClick={(e) => {
          if (!open) handleOpenDropdown(e);
          else handleClose();
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1.25,
          height: OPTION_HEIGHT,
          minHeight: OPTION_HEIGHT,
          borderRadius: theme.nawy.radius.lg,
          border: "1px solid",
          borderColor: hasSelection
            ? theme.nawy.colors.primitives.blue[500]
            : theme.nawy.colors.primitives.navy[200],
          bgcolor: hasSelection
            ? theme.nawy.colors.primitives.blue[50]
            : theme.nawy.colors.primitives.white[100],
          width: "100%",
          cursor: "pointer",
        }}
      >
        {hasSelection ? (
          <>
            <Tooltip
              title={selectedOptions.map((o) => o.label).join(", ")}
              placement="top"
              arrow
              enterDelay={500}
            >
              <Typography
                variant="caption"
                color={theme.nawy.colors.primitives.blue[500]}
                sx={{
                  ...theme.nawy.typography.english.variants.body.xs.regular,
                  flex: 1,
                  fontSize: "14px",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  lineHeight: OPTION_HEIGHT,
                }}
              >
                {getSummarizedText(selectedOptions)}
              </Typography>
            </Tooltip>
            <IconButton
              size="small"
              onClick={handleReset}
              sx={{ width: 24, height: 24, p: 0.25 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <>
            <Typography
              variant="caption"
              sx={{
                ...theme.nawy.typography.english.variants.body.xs.regular,
                fontSize: "14px",
                color: theme.nawy.colors.primitives.grey[500],
                ...customPlaceholderSx,
              }}
            >
              {placeholder}
            </Typography>
            <KeyboardArrowDownIcon fontSize="small" />
          </>
        )}
      </Box>
    );

    return (
      <Box
        sx={{ display: "inline-flex", alignItems: "center", width: "100%" }}
      >
        <LabelWithRequired
          label={label}
          required={required || error}
          sx={theme.nawy.typography.english.variants.body.md.regular}
        />

        {renderActionableSummaryField()}

        <CustomActionableSummaryPopper
          open={open}
          anchorEl={anchorRef.current}
        >
          <ClickAwayListener
            onClickAway={(event) => {
              const target = event.target as Node;
              if (anchorRef.current?.contains(target)) return;
              handleCancel();
            }}
          >
            <Box>
              <ActionableSummaryDropdown
                options={options}
                tempValue={tempValue}
                onToggle={handleOptionToggle}
                onReset={() => {
                  handleReset();
                  setInputValue("");
                }}
                onCancel={handleCancel}
                onApply={handleApply}
                handleKeyDown={handleKeyDown}
                highlightedIndex={highlightedIndex}
                setHighlightedIndex={setHighlightedIndex}
                dropdownRef={dropdownRef}
                inputValue={inputValue}
                setInputValue={setInputValue}
                debouncedSearch={debouncedSearch}
                loading={loading || false}
                renderOption={renderOption}
              />
            </Box>
          </ClickAwayListener>
        </CustomActionableSummaryPopper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: `${row ? "center" : "start"}`,
        width: "100%",
        flexDirection: `${row ? "row" : "column"}`,
        gap: 0.5,
      }}
    >
      <LabelWithRequired
        label={label}
        required={required || error}
        sx={{ ...theme.nawy.typography.english.variants.body.xs.regular, ...labelSx }}
      />

      <Autocomplete
        ref={autocompleteRef}
        options={filteredOptionsDefault}
        getOptionLabel={(option: Option) => option?.label ?? ""}
        filterOptions={
          disableClientSideFiltering || (handleSearch && uniqueOptionsOnly)
            ? (options) => options
            : undefined
        }
        isOptionEqualToValue={(option, value) =>
          uniqueOptionsOnly
            ? option.label === value.label
            : option.value === value.value
        }
        getOptionDisabled={(option) => option.disabled === true}
        disableClearable={disableClearable || (!multiple && !autocompleteValue)}
        disabled={disabled}
        value={uniqueOptionsOnly ? uniqueAutocompleteValue : autocompleteValue}
        onChange={handleChange}
        popupIcon={<KeyboardArrowDownIcon />}
        multiple={multiple}
        fullWidth={fullWidth}
        disableCloseOnSelect={multiple && !closeOnSelect}
        loading={loading}
        open={open}
        onOpen={() => {
          setOpen(true);
          onOpen?.();
        }}
        onClose={() => {
          setInputValue("");
          handleClose();
        }}
        autoHighlight={true}
        disableListWrap={false}
        selectOnFocus
        handleHomeEndKeys
        clearOnBlur
        inputValue={inputValue}
        onInputChange={(_, newValue, reason) => {
          if (reason === "input") {
            setInputValue(newValue);
            debouncedSearch(newValue);
          } else if (reason === "reset" && !open) {
            setInputValue(newValue);
          }
        }}
        PopperComponent={popperMinWidth ? createCustomPopper(popperMinWidth) : CustomPopper}
        sx={{
          "& .MuiAutocomplete-option": {
            maxHeight: OPTION_HEIGHT,
            alignItems: "center",
            fontFamily: "inherit",
            fontSize: "1rem",
            borderBottom: "1px solid #e0e0e0",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          "& .MuiAutocomplete-option:hover": {
            backgroundColor: "#f5f7fa",
          },
          "& .MuiAutocomplete-option:last-child": {
            borderBottom: "none",
          },
          "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
            padding: "4px",
          },
          "& .MuiAutocomplete-option[aria-selected='true']": {
            backgroundColor: "#e3f2fd !important",
            fontWeight: "500",
          },
        }}
        renderOption={(props, option) => {
          const isHighlighted =
            dropdownOptions.indexOf(option) === highlightedIndex;
          return (
            <li
              {...props}
              key={option.value}
              style={{
                backgroundColor: isHighlighted
                  ? "#e3f2fd"
                  : option.color
                  ? Color(option.color).alpha(0.2).toString()
                  : "transparent",
                fontWeight: isHighlighted
                  ? "500"
                  : nawyTheme.nawy.typography.english.variants.body.md.regular
                    .fontWeight,
                fontFamily: nawyTheme.nawy.typography.english.fontFamily,
                fontSize:
                  nawyTheme.nawy.typography.english.variants.body.md.regular
                    .fontSize,
                borderBottom: "1px solid #e0e0e0",
                boxSizing: "border-box",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                height: OPTION_HEIGHT,
                maxHeight: OPTION_HEIGHT,
                minHeight: OPTION_HEIGHT,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Tooltip
                title={showTooltip ? option.label : undefined}
                placement="right"
                arrow
                enterDelay={200}
                leaveDelay={200}
              >
                <span
                  style={{
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: option.color
                      ? Color(option.color).darken(0.5).toString()
                      : "#000000",
                  }}
                >
                  {renderOption ? renderOption(option) : option.label}
                </span>
              </Tooltip>
            </li>
          );
        }}
        renderTags={(value, getTagProps) => {
          if (!multiple) return null;
          return (value as Option[]).map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.value}
              label={option.label}
              deleteIcon={<CloseIcon style={{ fontSize: "1rem" }} />}
              sx={{
                backgroundColor: option.color || "#e0e0e0",
                color: "#222",
                fontSize: "0.75rem",
                marginRight: "8px",
                borderRadius: "14px",
                height: "20px",
                ...chipSx,
              }}
            />
          ));
        }}
        renderInput={(params) => {
          // Get the selected option's color
          const selectedOption = Array.isArray(autocompleteValue)
            ? undefined
            : autocompleteValue;

          const selectedColor = selectedOption?.color;
          const backgroundColor = selectedColor
            ? Color(selectedColor).alpha(0.2).toString()
            : "white";
          const textColor = selectedColor
            ? Color(selectedColor).darken(0.5).toString()
            : "inherit";

          return (
            <TextField
              {...params}
              error={error}
              helperText={helperText}
              placeholder={placeholder}
              variant="outlined"
              size="small"
              fullWidth
              sx={{
                backgroundColor,
                width: "100%",
                borderRadius: theme.nawy.radius.lg,
                "& .MuiOutlinedInput-root": {
                  borderRadius: theme.nawy.radius.lg,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.nawy.colors.semantic.border.primary,
                },
                "& .MuiInputBase-input": {
                  padding: `0 ${theme.nawy.spacing.xs}`,
                  color: textColor,
                },
                ...customPlaceholderSx,
              }}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: loading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : (
                    <>{params.InputProps.endAdornment}</>
                  ),
                },
              }}
            />
          );
        }}
      />
    </Box>
  );
};

export default CustomAutoComplete;
